'use client';

import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import { Icon } from '../Icon';
import {
  listVariants,
  listItemVariants,
  type ListVariantProps,
} from './list.variants';

export type BulletStyle = 'none' | 'bullet' | 'check' | 'square';

export interface ListProps
  extends React.HTMLAttributes<HTMLUListElement | HTMLOListElement>,
    ListVariantProps {
  asChild?: boolean;
  variant?: 'unordered' | 'ordered';
  bulletStyle?: BulletStyle;
  children: React.ReactNode;
}

export interface ListItemProps extends React.HTMLAttributes<HTMLLIElement> {
  asChild?: boolean;
  children: React.ReactNode;
}

const ListContext = React.createContext<{ bulletStyle: BulletStyle }>({
  bulletStyle: 'check',
});

export const List = React.forwardRef<
  HTMLUListElement | HTMLOListElement,
  ListProps
>(
  (
    {
      className,
      asChild = false,
      variant = 'unordered',
      bulletStyle = 'check',
      children,
      ...props
    },
    ref,
  ) => {
    const isOrdered = variant === 'ordered';
    const Comp = asChild ? Slot : isOrdered ? 'ol' : 'ul';

    return (
      <ListContext.Provider value={{ bulletStyle }}>
        <Comp
          className={cn(listVariants({ bulletStyle }), className)}
          ref={ref as React.Ref<HTMLUListElement & HTMLOListElement>}
          role='list'
          {...props}
        >
          {children}
        </Comp>
      </ListContext.Provider>
    );
  },
);

List.displayName = 'List';

export const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  ({ className, asChild = false, children, ...props }, ref) => {
    const { bulletStyle } = React.useContext(ListContext);

    if (asChild) {
      return (
        <Slot
          className={cn(listItemVariants(), className)}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <li
        className={cn(
          listItemVariants(),
          bulletStyle === 'check' && 'flex items-center gap-2',
          className,
        )}
        ref={ref}
        {...props}
      >
        {bulletStyle === 'check' && (
          <Icon icon='circle-check' size='sm' color='primary' aria-hidden />
        )}
        {bulletStyle === 'check' ? (
          <span className='flex-1'>{children}</span>
        ) : (
          children
        )}
      </li>
    );
  },
);

ListItem.displayName = 'ListItem';
